import { Card, CardContent, Grid2, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Description from '@components/Assessments/Common/Description';
import RangeList from '@components/Assessments/Common/RangeList';
import RangeSection from '@components/Assessments/Common/RangeSection';
import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import {
  COUGH_LOW_DISEASE_LIKELIHOOD,
  COUGH_MID_DISEASE_LIKELIHOOD,
  type CoughScanDisease,
} from '@constants/scan';
import { CoughScanField } from '@constants/scan';

import useCoughScan from '@hooks/useCoughScan';
import useReferenceRange from '@hooks/useReferenceRange';
import { useScanResults } from '@hooks/useScanResults';

import { formatDate } from '@utils/format';
import { capitalize } from '@utils/format';

import { ScanType } from 'generated/types';
import { ReferenceRangeRiskLevel, type ScanResultModel } from 'generated/types';

const messages = defineMessages({
  probability: {
    id: 'coughScanDrilldown.probability.title',
    defaultMessage: '{level} Probability',
  },
  low: {
    id: 'coughScanDrilldown.probability.low',
    defaultMessage: 'Low',
  },
  medium: {
    id: 'coughScanDrilldown.probability.medium',
    defaultMessage: 'Medium',
  },
  high: {
    id: 'coughScanDrilldown.probability.high',
    defaultMessage: 'High',
  },
  likelihood: {
    id: 'coughScanDrilldown.likelihood',
    defaultMessage:
      '{percentage} - {isLikely, select, true{Likely} other{Unlikely}}',
  },
  hasCondition: {
    id: 'coughScanDrilldown.hasCondition',
    defaultMessage:
      '{isLikely, select, true{Likely} other{Unlikely}} to have condition',
  },
  consultPhysician: {
    id: 'coughScanDrilldown.consultPhysician',
    defaultMessage:
      'Consult a physician. Testing may be required as per your physician’s advice.',
  },
  date: {
    id: 'coughScanDrilldown.scanDate',
    defaultMessage: 'Scan Date: {date}',
  },
  disclaimer: {
    id: 'scanMetrics.disclaimer',
    defaultMessage:
      'The {type} scan contains estimations related to potential health risks. It is not intended to diagnose, treat, cure or prevent disease. Please consult your doctor for further interpretation of this report.',
  },
});

/**
 * Scan result drilldown (used for cough scans only atm)
 * @returns Drilldown view of a scan result
 */

interface ScanResultDrilldownProps {
  data?: ScanResultModel;
  loading: boolean;
}

export default function ScanResultDrilldownContent({
  data,
  loading,
}: ScanResultDrilldownProps) {
  const { formatMessage } = useIntl();
  const { coughScanMap } = useCoughScan();
  const { friendly_type, is_sharing } = useScanResults();
  const { parameterMap } = useReferenceRange();

  return (
    <ContentLoader loading={loading}>
      <Stack my={1}>
        <Typography
          color="text.secondary"
          sx={{
            textAlign: 'center',
            color: ({ palette }) =>
              is_sharing ? palette.grey.A400 : 'text.secondary',
          }}
          variant="body2"
        >
          {formatMessage(messages.disclaimer, {
            type: friendly_type.toLowerCase(),
          })}
        </Typography>
        <Typography
          sx={{
            textAlign: 'right',
            color: ({ palette }) =>
              is_sharing ? palette.grey.A400 : 'text.secondary',
          }}
          variant="body1"
        >
          {formatMessage(messages.date, {
            date: formatDate({ date: data?.date }),
          })}
        </Typography>
      </Stack>

      <Stack spacing={2}>
        {(data?.cough_results?.disease_likelihoods ?? []).map(
          ({ disease_code, disease_name, likelihood: _likelihood }) => {
            const likelihood = _likelihood ?? 0;
            const disease = coughScanMap.get(disease_code as CoughScanDisease);
            const title =
              disease?.get(CoughScanField.Text) ?? capitalize(disease_name);
            const description = disease?.get(CoughScanField.Description) ?? '';

            const condition_likely = likelihood >= COUGH_LOW_DISEASE_LIKELIHOOD;
            const friendly_likelihood = `${likelihood}%`;
            let level = ReferenceRangeRiskLevel.ideal;
            let friendly_level: {
              id: string;
              defaultMessage: string;
            } = messages.low;
            if (likelihood >= COUGH_MID_DISEASE_LIKELIHOOD) {
              level = ReferenceRangeRiskLevel.high;
              friendly_level = messages.high;
            } else if (likelihood >= COUGH_LOW_DISEASE_LIKELIHOOD) {
              level = ReferenceRangeRiskLevel.moderate;
              friendly_level = messages.medium;
            } else {
              level = ReferenceRangeRiskLevel.normal;
              friendly_level = messages.low;
            }

            const { colour } = parameterMap[level];

            return (
              <Card key={`${ScanType.respiratory}-${disease_code}-card`}>
                <CardContent>
                  <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Description
                        explanation={description}
                        loading={loading}
                        result={
                          <Stack
                            alignItems="center"
                            direction="row"
                            columnGap={0.5}
                          >
                            {formatMessage(messages.likelihood, {
                              percentage: friendly_likelihood,
                              isLikely: condition_likely,
                            })}
                            <Icon
                              color={colour}
                              name="square"
                              size="lg"
                              type="fas"
                            />
                          </Stack>
                        }
                        title={title}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <RangeSection
                        loading={loading}
                        subtitle={formatMessage(messages.hasCondition, {
                          isLikely: condition_likely,
                        })}
                      >
                        {/* {condition_likely && (
                          <Typography fontWeight="bold" variant="body2">
                            {formatMessage(messages.consultPhysician)}
                          </Typography>
                        )} */}
                        <RangeList
                          code={disease_code}
                          level={level}
                          levelText={formatMessage(messages.probability, {
                            level: formatMessage(friendly_level),
                          })}
                          isInRange={true}
                          range={[friendly_likelihood]}
                        />
                      </RangeSection>
                    </Grid2>
                  </Grid2>
                </CardContent>
              </Card>
            );
          }
        )}
      </Stack>
    </ContentLoader>
  );
}
